import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import ConstituentsTable from '../../components/ConstituentsTable/ConstituentsTable';

import { USER_ROLES } from '../../../util/constants/AppConstants';
import { getConstituents, getCouncillorConstituents } from '../../../http/constituentAPI';

import './Constituents.scss';

const Constituents = () => {
  const [constituentsType, setConstituentsType] = useState('Active');
  const [loading, setLoading] = useState(false);
  const [constituents, setConstituents] = useState([]);
  const [activeConstituents, setActiveConstituents] = useState([]);

  const config = useSelector((state) => state.config.config);

  const { userInfo } = useOutletContext();

  const councilorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;
  const isCouncillor = userInfo && userInfo.role === councilorRoleValue;

  const updateActiveConstituents = (constituents) => {
    if (constituentsType === 'Pending') {
      setActiveConstituents(constituents.filter((c) => c.pending));
    } else {
      setActiveConstituents(constituents.filter((c) => !c.pending));
    }
  };

  const handleGetConstituentsData = async (userId) => {
    setLoading(true);

    const res = isCouncillor ? await getCouncillorConstituents(config, userId) : await getConstituents(config, userId);

    if (res.status === 200) {
      setConstituents(res.data.constituents);
      updateActiveConstituents(res.data.constituents);
      setLoading(false);
    } else {
      console.log('ERROR: Grabbing Constituents');
    }
  };

  useEffect(() => {
    if (config.api && userInfo) {
      handleGetConstituentsData(userInfo.id);
    }
  }, [config, userInfo]);

  useEffect(() => {
    updateActiveConstituents(constituents);
  }, [constituentsType]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: 'name',
        header: 'Name',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
      {
        accessorKey: 'contact_count',
        header: 'Contact count',
        minSize: 110,
        grow: 1,
        enableSorting: true,
        filterVariant: 'range',
      },
      {
        accessorFn: (row) => {
          if (isCouncillor) {
            const wardNumber = row.ward_label;
            return wardNumber ? `Ward - ${wardNumber}` : null;
          }
          const pollNumber = row.poll_label;
          return pollNumber ? `Poll - ${pollNumber}` : null;
        },
        id: isCouncillor ? 'ward_label' : 'poll_label',
        header: isCouncillor ? 'Ward' : 'Poll',
        minSize: 110,
        grow: 1,
        filterVariant: 'select',
      },
      {
        accessorKey: 'postal_code',
        header: 'Postal code',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
      {
        accessorKey: 'address',
        header: 'Address',
        minSize: 150,
        grow: 5,
        enableSorting: false,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
    ];
    return baseColumns;
  }, [isCouncillor]);

  return (
    <div className="constituents">
      <div className="activity-map__tabs">
        <div
          className={`activity-map__tab ${constituentsType === 'Active' ? 'active' : ''}`}
          onClick={() => setConstituentsType('Active')}
        >
          Active
        </div>
        <div
          className={`activity-map__tab ${constituentsType === 'Pending' ? 'active' : ''}`}
          onClick={() => setConstituentsType('Pending')}
        >
          Pending
        </div>
      </div>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ConstituentsTable
          data={activeConstituents}
          columns={columns}
          loading={loading}
          handleGetConstituentsData={handleGetConstituentsData}
          pending={constituentsType === 'Pending'}
        />
      </LocalizationProvider>
    </div>
  );
};

export default Constituents;
